/**
 * The rules Settings → Messaging checks as staff type, the same ones the database applies
 * (`set_messaging_keyword`, `private.check_twilio_configuration`). The database stays the authority: these only say
 * early, in the same words, what it would refuse.
 */

/** Words carriers reserve for opting out, resubscribing and help (`private.is_reserved_sms_keyword`). */
export const RESERVED_WORDS = [
  "STOP",
  "STOPALL",
  "UNSUBSCRIBE",
  "CANCEL",
  "END",
  "QUIT",
  "REVOKE",
  "OPTOUT",
  "START",
  "UNSTOP",
  "HELP",
  "INFO",
] as const;

/** Each reply stays within two segments' worth of characters, as the database requires. */
export const MAX_REPLY_LENGTH = 320;

export interface KeywordDraft {
  readonly keyword: string;
  readonly confirmWord: string;
  readonly welcome: string;
  readonly confirmation: string;
  /** The church's join page; "{join}" in the confirmation becomes it with the person's one-time code (#338). */
  readonly joinUrl: string;
}

/** What "{join}" adds when it is filled: "?j=" and a 32-digit code after the page's address. */
export const JOIN_CODE_LENGTH = 35;

/** The confirmation as it is sent, with "{join}" filled by the join page and a placeholder code. */
export function confirmationAsSent(draft: Pick<KeywordDraft, "confirmation" | "joinUrl">): string {
  const url = draft.joinUrl.trim();
  if (url === "") return draft.confirmation;
  return draft.confirmation
    .split("{join}")
    .join(`${url}${url.includes("?") ? "&" : "?"}j=${"0".repeat(32)}`);
}

/** What the database would refuse about a keyword, in its own terms; empty when it would accept it. */
export function keywordProblems(draft: KeywordDraft): string[] {
  const problems: string[] = [];
  const keyword = draft.keyword.trim().toUpperCase();
  const confirm = draft.confirmWord.trim().toUpperCase();
  const word = /^[A-Z0-9]{2,20}$/;
  if (!word.test(keyword) || !word.test(confirm)) {
    problems.push("A keyword and a confirm word are 2 to 20 letters or digits, with no spaces.");
  }
  const reserved = [keyword, confirm].filter((w) =>
    (RESERVED_WORDS as readonly string[]).includes(w),
  );
  if (reserved.length > 0) {
    problems.push(
      `${reserved.join(" and ")} ${reserved.length > 1 ? "are" : "is"} reserved by the carriers (STOP, START, HELP and the like).`,
    );
  }
  if (keyword !== "" && keyword === confirm) {
    problems.push("The confirm word must differ from the keyword.");
  }
  const welcome = draft.welcome.toUpperCase();
  if (draft.welcome.trim() === "") {
    problems.push("Write the welcome.");
  } else {
    if (confirm !== "" && !welcome.includes(confirm)) {
      problems.push(`The welcome asks them to reply ${confirm}.`);
    }
    if (!welcome.includes("STOP") || !welcome.includes("HELP")) {
      problems.push("The welcome says how to stop (STOP) and get help (HELP).");
    }
  }
  if (draft.welcome.length > MAX_REPLY_LENGTH) {
    problems.push(`The welcome is at most ${String(MAX_REPLY_LENGTH)} characters.`);
  }
  if (draft.confirmation.trim() === "") {
    problems.push("Write the confirmation.");
  } else if (!draft.confirmation.toUpperCase().includes("STOP")) {
    problems.push("The confirmation says how to stop (STOP).");
  }
  const joinUrl = draft.joinUrl.trim();
  if (joinUrl !== "" && (!/^https:\/\/[^\s/?#]+\S*$/.test(joinUrl) || joinUrl.length > 200)) {
    problems.push("The join page is an https address of at most 200 characters.");
  }
  if (draft.confirmation.includes("{join}") && joinUrl === "") {
    problems.push("A confirmation with {join} needs the join page's address.");
  }
  if (confirmationAsSent(draft).length > MAX_REPLY_LENGTH) {
    problems.push(
      `The confirmation is at most ${String(MAX_REPLY_LENGTH)} characters, with its link in place.`,
    );
  }
  return problems;
}

/** A Messaging Service SID as Twilio issues it: MG and 32 lowercase hex digits. */
export function isMessagingServiceSid(value: string): boolean {
  return /^MG[0-9a-f]{32}$/.test(value.trim());
}

/** An account SID: AC and 32 lowercase hex digits (an API key starts SK and is refused). */
export function isAccountSid(value: string): boolean {
  return /^AC[0-9a-f]{32}$/.test(value.trim());
}

/** A sending number in international form, like +15551234567. */
export function isE164(value: string): boolean {
  return /^\+[1-9][0-9]{7,14}$/.test(value.trim());
}

/**
 * The address Twilio sends a church's incoming texts to: HoW's own (`/hooks/twilio` on the platform's address, owner
 * 2026-10-02), never Supabase's.
 */
export function incomingTextsAddress(platformBaseDomain: string, connectionId: string): string {
  return `https://${platformBaseDomain}/hooks/twilio?kind=inbound&ti=${connectionId}`;
}
